import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { apiErrToStr } from "@/lib/api";

export default function Auth() {
  const { login, register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "login") await login(f.email, f.password);
      else await register(f.email, f.password, f.name);
      toast.success(tab === "login" ? "Welcome back!" : "Account created — Har Har Mahadev!");
      nav("/");
    } catch (err) { toast.error(apiErrToStr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl card-shadow border border-gold/20 p-6 md:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-full bg-indigo-deep items-center justify-center mb-2">
            <span className="font-display text-gold text-2xl">॥</span>
          </div>
          <h1 className="font-display text-2xl text-indigo-deep">Welcome to IndiaDham</h1>
          <p className="text-sm text-indigo-deep/60">Sign in to manage your bookings and inquiries</p>
        </div>
        <Button data-testid="google-signin-btn" type="button" variant="outline" onClick={loginWithGoogle} className="w-full h-11 mb-4 border-gold/40 text-indigo-deep hover:bg-gold/10 font-semibold">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
          Continue with Google
        </Button>
        <div className="flex items-center gap-3 mb-4 text-xs text-indigo-deep/40"><div className="flex-1 h-px bg-gold/20" />or use email<div className="flex-1 h-px bg-gold/20" /></div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full mb-4">
            <TabsTrigger data-testid="tab-login" value="login">Sign in</TabsTrigger>
            <TabsTrigger data-testid="tab-register" value="register">Create account</TabsTrigger>
          </TabsList>
          <TabsContent value="register">
            <Label>Full name</Label>
            <Input data-testid="auth-name" className="mb-3" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </TabsContent>
          <div>
            <Label>Email</Label>
            <Input data-testid="auth-email" type="email" className="mb-3" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <Label>Password</Label>
            <Input data-testid="auth-password" type="password" className="mb-4" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            <Button data-testid="auth-submit" onClick={submit} disabled={busy} className="w-full h-11 bg-indigo-deep hover:bg-indigo text-white font-semibold">
              {busy ? "Please wait…" : tab === "login" ? "Sign in" : "Create account"}
            </Button>
          </div>
        </Tabs>
        <p className="text-xs text-center text-indigo-deep/50 mt-4">By continuing you agree to our Terms & Privacy Policy.</p>
      </div>
    </div>
  );
}
